import { useContext, useState } from "react";
import { Button, Form } from "react-bootstrap";
import styled from "styled-components";

import SearchContext from "common/store/SearchContext";

import { MyDatePicker } from "common/components/atoms/MyDatePicker";
import { addMonths, daysInMonth } from "utils/DateTools";

type Props = {
  pageTitle: string;
  onSearch: (from: Date, to: Date) => void;
};

export const MapTopPanel = (props: Props) => {
  const { pageTitle, onSearch } = props;
  const { searchFrom, searchTo, setSearchFrom, setSearchTo, resetSearchRange } =
    useContext(SearchContext);
  const [keepMonthEnd, setKeepMonthEnd] = useState(true);

  const handleFromChanged = (val?: Date) => {
    if (!val) return;
    setSearchFrom(val);
    onSearch(val, searchTo);
  };

  const handleToChanged = (val?: Date) => {
    if (!val) return;
    setSearchTo(val);
    onSearch(searchFrom, val);
  };

  const shiftPeriod = (months: number, years: number) => {
    const offsetMonths = months + years * 12;
    const newFrom = addMonths(searchFrom, offsetMonths);
    let newTo = addMonths(searchTo, offsetMonths);

    if (keepMonthEnd && searchTo.getDate() === daysInMonth(searchTo)) {
      newTo = new Date(newTo.getFullYear(), newTo.getMonth() + 1, 0);
    }

    setSearchFrom(newFrom);
    setSearchTo(newTo);

    onSearch(newFrom, newTo);
  };

  const resetPeriod = () => {
    const { from: newFrom, to: newTo } = resetSearchRange();
    onSearch(newFrom, newTo);
  };

  return (
    <STopDiv>
      <STitleDiv>
        <span className="h4">{pageTitle}</span>
      </STitleDiv>

      <SPeriodDiv>
        <Form.Label className="h6 mb-0">期間</Form.Label>
        <MyDatePicker
          value={searchFrom}
          onChange={handleFromChanged}
          canClear={false}
          style={{ width: "120px" }}
        />
        <span>〜</span>
        <MyDatePicker
          value={searchTo}
          onChange={handleToChanged}
          canClear={false}
          style={{ width: "120px" }}
        />
        <SIconButton variant="outline-info" title="期間をリセット" onClick={resetPeriod}>
          <i className="fas fa-rotate-left" />
        </SIconButton>
        <SIconButton variant="outline-info" title="1年戻す" onClick={() => shiftPeriod(0, -1)}>
          <i className="fas fa-angles-left" />
        </SIconButton>
        <SIconButton variant="outline-info" title="1ヵ月戻す" onClick={() => shiftPeriod(-1, 0)}>
          <i className="fas fa-angle-left" />
        </SIconButton>
        <SIconButton variant="outline-info" title="1ヵ月進める" onClick={() => shiftPeriod(1, 0)}>
          <i className="fas fa-angle-right" />
        </SIconButton>
        <SIconButton variant="outline-info" title="1年進める" onClick={() => shiftPeriod(0, 1)}>
          <i className="fas fa-angles-right" />
        </SIconButton>
        <Form.Check
          type="checkbox"
          label="月末を維持"
          checked={keepMonthEnd}
          onChange={(e) => setKeepMonthEnd(e.currentTarget.checked)}
          className="mb-0"
          title="期間の終わりが月末の場合、その状態を維持します"
        />
      </SPeriodDiv>
    </STopDiv>
  );
};

const STopDiv = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 2rem;
`;

const STitleDiv = styled.div`
  display: flex;
  margin: 1rem 0.5rem 0 0.5rem;
  justify-content: space-between;
  flex-wrap: wrap;
  @media screen and (max-width: 768px) {
    margin-right: 1.5rem;
  }
`;

const SPeriodDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  position: relative;
  z-index: 1000;
`;

const SIconButton = styled(Button)`
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
